import React, { createContext, useContext, useMemo } from "react";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { Auth, Issuer } from "../types/auth.types";
import { extractHmacFromUrl } from "../utils/extractHmacFromUrl";
import { HmacContext, HmacProvider } from "./HmacContext";
import { JwtContext, JwtProvider } from "./JwtContext";

type ProviderProps = Readonly<{ children: React.ReactNode }>;

export const AuthContext = createContext<Auth | undefined>(undefined);

const DEFAULT_AUTH: Auth = {
  issuer: Issuer.NONE,
  isAuthenticated: false,
  isLoading: false,
  credentials: null
};

const determineIssuer = (): Issuer => {
  if (
    extractHmacFromUrl() ||
    sessionStorage.getItem(STORAGE_KEYS.HMAC_HEADERS)
  ) {
    return Issuer.HMAC;
  }

  if (window.authToken || sessionStorage.getItem(STORAGE_KEYS.JWT_TOKEN)) {
    return Issuer.JWT;
  }

  return Issuer.NONE;
};

const AuthContextProvider = ({
  children
}: ProviderProps): React.ReactElement => {
  const hmacAuth = useContext(HmacContext);
  const jwtAuth = useContext(JwtContext);

  const activeAuth = useMemo(() => {
    // Prioritize loading state from providers
    if (jwtAuth?.isLoading) return jwtAuth;
    if (hmacAuth?.isLoading) return hmacAuth;

    const issuer = determineIssuer();
    const authMap: Record<Issuer, Auth> = {
      [Issuer.HMAC]: hmacAuth ?? DEFAULT_AUTH,
      [Issuer.JWT]: jwtAuth ?? DEFAULT_AUTH,
      [Issuer.NONE]: jwtAuth ?? hmacAuth ?? DEFAULT_AUTH
    };

    return authMap[issuer];
  }, [hmacAuth, jwtAuth]);

  return (
    <AuthContext.Provider value={activeAuth}>{children}</AuthContext.Provider>
  );
};

export const AuthProvider = ({
  children
}: ProviderProps): React.ReactElement => {
  const issuer = determineIssuer();
  const CredentialProvider =
    issuer === Issuer.HMAC ? HmacProvider : JwtProvider;

  return (
    <CredentialProvider>
      <AuthContextProvider>{children}</AuthContextProvider>
    </CredentialProvider>
  );
};
